import { useCallback, useEffect, useState } from 'react';
import {
  ref as storageRef,
  uploadBytesResumable,
  UploadTask,
} from 'firebase/storage';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { storage, db } from '../lib/firebase';

// Browser crypto.randomUUID fallback for older Safari — extremely unlikely
// in the Valutten admin-facing context (Chromium-class only) but cheap.
function generateUploadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export type ImportKind = 'contacts' | 'suppressions';

export interface ImportStatus {
  uploadId: string;
  kind: ImportKind;
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'failed';
  rowsProcessed: number;
  rowsSkipped: number;
  rowsFailed: number;
  rowsTotal: number;
  truncated: boolean;
  error: string | null;
  errors: Array<{ row: number; reason: string }>;
  startedAt: string | null;
  completedAt: string | null;
  uploadProgress: number; // 0..1, client-side only (Firebase upload progress)
}

interface State {
  start: (file: File, kind: ImportKind) => Promise<string>;
  status: ImportStatus | null;
  reset: () => void;
}

function tsToIso(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return null;
}

// Client-side CSV import orchestrator. Uploads the file to Firebase Storage
// at the well-known path that processContactImport listens for, then
// subscribes to the status doc for live progress until the backend reports
// completed/failed.
export function useContactImport(companyId: string | undefined): State {
  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [uploadTask, setUploadTask] = useState<UploadTask | null>(null);

  const reset = useCallback(() => {
    setStatus(null);
    if (uploadTask) {
      try {
        uploadTask.cancel();
      } catch {
        // already complete or cancelled
      }
    }
    setUploadTask(null);
  }, [uploadTask]);

  // Subscribe to the status doc whenever we have an active upload.
  useEffect(() => {
    if (!companyId || !status?.uploadId) return;
    const unsub = onSnapshot(
      doc(db, `companies/${companyId}/contact_imports/${status.uploadId}`),
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() as Record<string, unknown>;
        setStatus((prev) =>
          prev
            ? {
                ...prev,
                status: ((d.status as ImportStatus['status']) ?? prev.status),
                rowsProcessed: (d.rowsProcessed as number) ?? prev.rowsProcessed,
                rowsSkipped: (d.rowsSkipped as number) ?? prev.rowsSkipped,
                rowsFailed: (d.rowsFailed as number) ?? prev.rowsFailed,
                rowsTotal: (d.rowsTotal as number) ?? prev.rowsTotal,
                truncated: Boolean(d.truncated),
                error: (d.error as string | null) ?? prev.error,
                errors:
                  (d.errors as Array<{ row: number; reason: string }>) ??
                  prev.errors,
                startedAt: tsToIso(d.startedAt) ?? prev.startedAt,
                completedAt: tsToIso(d.completedAt) ?? prev.completedAt,
              }
            : prev
        );
      }
    );
    return () => unsub();
  }, [companyId, status?.uploadId]);

  const start = useCallback(
    async (file: File, kind: ImportKind) => {
      if (!companyId) throw new Error('companyId required');

      const uploadId = generateUploadId();
      const path = `companies/${companyId}/contact-imports/${uploadId}/${kind}.csv`;
      const ref = storageRef(storage, path);

      setStatus({
        uploadId,
        kind,
        status: 'uploading',
        rowsProcessed: 0,
        rowsSkipped: 0,
        rowsFailed: 0,
        rowsTotal: 0,
        truncated: false,
        error: null,
        errors: [],
        startedAt: null,
        completedAt: null,
        uploadProgress: 0,
      });

      const task = uploadBytesResumable(ref, file, {
        contentType: file.type || 'text/csv',
        customMetadata: { kind, uploadedAt: new Date().toISOString() },
      });
      setUploadTask(task);

      return new Promise<string>((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => {
            const pct =
              snap.totalBytes > 0
                ? snap.bytesTransferred / snap.totalBytes
                : 0;
            setStatus((prev) =>
              prev ? { ...prev, uploadProgress: pct } : prev
            );
          },
          (err) => {
            setStatus((prev) =>
              prev
                ? { ...prev, status: 'failed', error: err.message }
                : prev
            );
            reject(err);
          },
          () => {
            // Upload complete → backend takes over. The onSnapshot listener
            // will pick up the status transitions from here.
            setStatus((prev) =>
              prev ? { ...prev, uploadProgress: 1, status: 'queued' } : prev
            );
            resolve(uploadId);
          }
        );
      });
    },
    [companyId]
  );

  return { start, status, reset };
}
